import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sparkles, 
  FileText, 
  Calendar, 
  Users, 
  BookOpen, 
  Palette, 
  Target, 
  Zap,
  Check,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Play,
  Star,
  Clock,
  Layers,
  Wand2,
  GraduationCap,
  Printer,
  Monitor,
  Smartphone,
  Heart
} from "lucide-react";

// FAQ Accordion Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-2 border-orange-100 rounded-2xl mb-3 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button 
        onClick={onClick}
        className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-orange-50/50 transition-colors"
        data-testid={`faq-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-fredoka font-bold text-slate-800 pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-orange-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-slate-600 leading-relaxed border-t border-orange-100 font-nunito">
          <p className="pt-4">{answer}</p>
        </div>
      )}
    </div>
  );
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description, color = "orange" }) {
  const colors = {
    orange: "bg-orange-50 border-orange-200 hover:border-orange-300",
    blue: "bg-blue-50 border-blue-200 hover:border-blue-300",
    purple: "bg-purple-50 border-purple-200 hover:border-purple-300",
    green: "bg-green-50 border-green-200 hover:border-green-300",
    pink: "bg-pink-50 border-pink-200 hover:border-pink-300",
    yellow: "bg-yellow-50 border-yellow-200 hover:border-yellow-300",
  };
  const iconColors = {
    orange: "bg-orange-500",
    blue: "bg-blue-500",
    purple: "bg-purple-500",
    green: "bg-green-500",
    pink: "bg-pink-500",
    yellow: "bg-yellow-500",
  };
  
  return (
    <div className={`${colors[color]} border-2 rounded-3xl p-6 transition-all hover:shadow-lg hover:-translate-y-1`}>
      <div className={`w-14 h-14 ${iconColors[color]} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="font-fredoka text-xl font-bold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-600 font-nunito leading-relaxed">{description}</p>
    </div>
  );
}

// Use Case Card
function UseCaseCard({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-white border-2 border-orange-100 rounded-2xl hover:shadow-md transition-all hover:border-orange-200">
      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h4 className="font-fredoka font-bold text-slate-800 mb-1">{title}</h4>
        <p className="text-sm text-slate-600 font-nunito">{description}</p>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(0);

  const handleCTA = () => {
    navigate("/auth");
  };

  const faqs = [
    {
      question: "À qui s'adresse L'École de Navi ?",
      answer: "L'École de Navi est conçue pour les enseignants du primaire (maternelle et élémentaire). Elle s'adapte à tous les niveaux, de la petite section au CM2, et à toutes les matières."
    },
    {
      question: "Peut-on créer ses propres fiches pédagogiques ?",
      answer: "Oui ! L'éditeur visuel vous permet de créer des fiches personnalisées en quelques minutes. Ajoutez du texte, des images, des formes, utilisez les bibliothèques d'objets pédagogiques et personnalisez chaque élément."
    },
    {
      question: "Comment fonctionne la différenciation par élève ?",
      answer: "Vous pouvez définir des profils élèves avec leurs besoins spécifiques (dyslexie, trouble de l'attention, etc.). L'outil génère automatiquement des variantes adaptées de vos ressources pour chaque élève."
    },
    {
      question: "Peut-on imprimer les ressources créées ?",
      answer: "Absolument ! Toutes les fiches peuvent être exportées en PDF haute qualité pour impression. Vous pouvez aussi les projeter en classe ou les partager en format numérique."
    },
    {
      question: "Comment fonctionne le cahier-journal ?",
      answer: "Le cahier-journal vous permet de planifier vos séances sur la semaine. Chaque séance peut être liée à une ressource, des compétences du programme et des élèves cibles. Le drag & drop facilite la réorganisation."
    },
    {
      question: "L'IA est-elle obligatoire ?",
      answer: "Non, l'IA est une assistance optionnelle. Elle peut vous aider à générer des bases de fiches, suggérer des contenus ou adapter des ressources, mais vous gardez toujours le contrôle total sur vos créations."
    },
    {
      question: "Les modèles sont-ils vraiment prêts à l'emploi ?",
      answer: "Oui, notre bibliothèque contient des centaines de modèles créés par des enseignants. Lignes d'écriture, exercices de mathématiques, supports de lecture... Tous sont personnalisables en quelques clics."
    },
    {
      question: "Mes données et créations sont-elles sécurisées ?",
      answer: "Vos données sont stockées de manière sécurisée et restent votre propriété. Vous pouvez exporter et sauvegarder vos créations à tout moment."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-blue-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-orange-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="flex items-center gap-3">
              <img 
                src="/NaviLogo.png" 
                alt="L'École de Navi" 
                className="h-12 lg:h-14 w-auto drop-shadow-md"
              />
              <span className="font-fredoka font-bold text-xl lg:text-2xl text-slate-800 hidden sm:block">
                L'École de Navi
              </span>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <a href="#fonctionnalites" className="text-slate-600 hover:text-orange-500 font-nunito font-medium transition-colors">
                Fonctionnalités
              </a>
              <a href="#cas-usage" className="text-slate-600 hover:text-orange-500 font-nunito font-medium transition-colors">
                Cas d'usage
              </a>
              <a href="#faq" className="text-slate-600 hover:text-orange-500 font-nunito font-medium transition-colors">
                FAQ
              </a>
            </nav>

            <button
              onClick={handleCTA}
              data-testid="header-cta"
              className="bg-orange-500 hover:bg-orange-600 text-white font-fredoka font-bold py-2.5 px-5 lg:py-3 lg:px-6 rounded-full shadow-[0_4px_0_rgb(194,65,12)] hover:shadow-[0_2px_0_rgb(194,65,12)] hover:translate-y-[2px] transition-all text-sm lg:text-base"
            >
              Commencer
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-12 lg:pt-20 pb-16 lg:pb-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-yellow-100 border border-yellow-200 rounded-full px-4 py-1.5 mb-6">
                <Sparkles className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-fredoka font-bold text-yellow-700">Nouvelle version avec IA</span>
              </div>
              
              <h1 className="font-fredoka text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Le copilote pédagogique des{" "}
                <span className="text-orange-500">enseignants</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-slate-600 mb-8 font-nunito leading-relaxed">
                Créez des fiches pédagogiques, gérez votre cahier-journal et différenciez vos supports <strong className="text-slate-800">en quelques minutes</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <button
                  onClick={handleCTA}
                  data-testid="hero-cta-primary"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-fredoka font-bold py-4 px-8 rounded-full shadow-[0_6px_0_rgb(194,65,12)] hover:shadow-[0_3px_0_rgb(194,65,12)] hover:translate-y-[3px] transition-all flex items-center justify-center gap-2 text-lg"
                >
                  Essayer gratuitement
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => document.getElementById('fonctionnalites').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-cta-secondary"
                  className="bg-white hover:bg-orange-50 text-slate-700 font-fredoka font-bold py-4 px-8 rounded-full border-2 border-orange-200 hover:border-orange-300 transition-all flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5 text-orange-500" />
                  Découvrir
                </button>
              </div>

              {/* Micro-preuves */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {["Éditeur visuel", "Cahier-journal", "Différenciation", "Bibliothèques", "IA d'assistance"].map((item) => (
                  <span 
                    key={item}
                    className="inline-flex items-center gap-1.5 text-sm font-nunito font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm"
                  >
                    <Check className="w-3.5 h-3.5 text-green-500" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero Image - Mascotte Navi */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <img 
                  src="/LEcoleDeNavi3.png"
                  alt="Navi, la mascotte de L'École de Navi"
                  className="w-72 lg:w-96 h-auto drop-shadow-2xl mascot-float"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-2 -left-4 lg:-left-8 bg-white border-2 border-yellow-200 rounded-2xl px-4 py-2 shadow-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-fredoka font-bold text-slate-700">Gagnez 5h/semaine</span>
                  </div>
                </div>
                {/* Stars decoration */}
                <div className="absolute top-4 -right-4 lg:-right-8">
                  <div className="flex gap-1">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 mt-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problème / Promesse Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Le Problème */}
            <div className="bg-slate-50 border-2 border-slate-200 rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <img src="/LEcoleDeNavi7.png" alt="" className="w-16 h-16" />
                <h2 className="font-fredoka text-2xl lg:text-3xl font-bold text-slate-800">
                  Le quotidien de la préparation
                </h2>
              </div>
              <ul className="space-y-4">
                {[
                  "Des heures à chercher, créer et adapter des supports",
                  "Des outils éparpillés : Word, Pinterest, photocopies...",
                  "La différenciation qui prend un temps fou",
                  "Les mêmes ressources à refaire chaque année",
                  "Aucune cohérence entre préparation et séances"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-500 text-sm">✗</span>
                    </span>
                    <span className="text-slate-600 font-nunito">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* La Promesse */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <img src="/LEcoleDeNavi5.png" alt="" className="w-16 h-16" />
                <h2 className="font-fredoka text-2xl lg:text-3xl font-bold text-slate-800">
                  Avec L'École de Navi
                </h2>
              </div>
              <ul className="space-y-4">
                {[
                  "Créez des fiches en quelques minutes, pas des heures",
                  "Tout au même endroit : création, planification, ressources",
                  "Différenciation automatique selon les profils élèves",
                  "Réutilisez et adaptez vos créations facilement",
                  "Séances reliées aux compétences et aux ressources"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </span>
                    <span className="text-slate-700 font-nunito">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section id="fonctionnalites" className="py-20 lg:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="font-fredoka text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-nunito">
              Fiches pédagogiques, cahier-journal, gestion de classe, différenciation... 
              L'École de Navi centralise votre travail d'enseignant.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <FeatureCard 
              icon={Palette}
              title="Éditeur visuel de fiches"
              description="Créez des fiches professionnelles en glisser-déposer. Texte, images, formes, lignes d'écriture..."
              color="orange"
            />
            <FeatureCard 
              icon={Calendar}
              title="Cahier-journal"
              description="Planifiez vos séances sur la semaine. Reliez chaque séance à vos ressources et compétences."
              color="blue"
            />
            <FeatureCard 
              icon={BookOpen}
              title="Bibliothèques pédagogiques"
              description="Des centaines de modèles, cliparts et blocs prêts à l'emploi. Créés par des enseignants."
              color="green"
            />
            <FeatureCard 
              icon={Target}
              title="Différenciation par élève"
              description="Créez des variantes adaptées aux besoins de chaque élève : dyslexie, trouble de l'attention..."
              color="pink"
            />
            <FeatureCard 
              icon={Users}
              title="Gestion de classe"
              description="Gérez vos élèves, leurs profils et leurs besoins spécifiques depuis un tableau de bord clair."
              color="purple"
            />
            <FeatureCard 
              icon={Wand2}
              title="IA d'assistance"
              description="Génération de contenus, suggestions adaptées au niveau, aide à la différenciation..."
              color="yellow"
            />
          </div>
        </div>
      </section>

      {/* Pourquoi c'est différent */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-orange-500 to-orange-600 text-white overflow-hidden relative">
        {/* Decorative mascot */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-20 hidden lg:block">
          <img src="/LEcoleDeNavi4.png" alt="" className="w-80" />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-fredoka text-3xl lg:text-5xl font-bold mb-6">
            Pas juste un éditeur.<br />
            <span className="text-yellow-200">Un écosystème pédagogique complet.</span>
          </h2>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto mb-12 font-nunito">
            L'École de Navi relie vos ressources, vos séances, vos compétences et vos élèves 
            dans un seul outil pensé par et pour les enseignants.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 lg:gap-6">
            {[
              { icon: Layers, text: "Création" },
              { icon: Calendar, text: "Planification" },
              { icon: Target, text: "Différenciation" },
              { icon: GraduationCap, text: "Compétences" }
            ].map(({ icon: Icon, text }) => (
              <div 
                key={text}
                className="flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-full px-6 py-3"
              >
                <Icon className="w-5 h-5 text-yellow-200" />
                <span className="font-fredoka font-bold">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cas d'usage */}
      <section id="cas-usage" className="py-20 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="font-fredoka text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Comment l'utiliser au quotidien ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-nunito">
              De la préparation rapide d'une fiche à la gestion complète de votre semaine.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <UseCaseCard 
              icon={Zap}
              title="Créer une fiche en 5 minutes"
              description="Partez d'un modèle, personnalisez et exportez. C'est aussi simple que ça."
            />
            <UseCaseCard 
              icon={Calendar}
              title="Préparer sa semaine"
              description="Planifiez vos séances dans le cahier-journal avec un simple drag & drop."
            />
            <UseCaseCard 
              icon={Target}
              title="Adapter pour chaque élève"
              description="Générez des variantes automatiques selon les besoins spécifiques."
            />
            <UseCaseCard 
              icon={BookOpen}
              title="Retrouver ses ressources"
              description="Toutes vos créations organisées par matière, niveau et compétence."
            />
            <UseCaseCard 
              icon={Sparkles}
              title="Générer avec l'IA"
              description="Demandez à l'IA de vous proposer une base de fiche à personnaliser."
            />
            <UseCaseCard 
              icon={Printer}
              title="Imprimer ou projeter"
              description="Export PDF haute qualité pour impression ou projection en classe."
            />
          </div>
        </div>
      </section>

      {/* Multi-format Section avec illustration */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-fredoka text-3xl lg:text-4xl font-bold text-slate-800 mb-6">
                Vos ressources, partout
              </h2>
              <p className="text-lg text-slate-600 mb-8 font-nunito leading-relaxed">
                Créez une fois, utilisez de multiples façons. Vos fiches s'adaptent à tous vos besoins : 
                impression papier, projection TNI, partage numérique ou même exercices interactifs.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white border-2 border-blue-200 rounded-full px-4 py-2 shadow-sm">
                  <Printer className="w-5 h-5 text-blue-500" />
                  <span className="font-nunito font-medium">Impression PDF</span>
                </div>
                <div className="flex items-center gap-2 bg-white border-2 border-purple-200 rounded-full px-4 py-2 shadow-sm">
                  <Monitor className="w-5 h-5 text-purple-500" />
                  <span className="font-nunito font-medium">Projection TNI</span>
                </div>
                <div className="flex items-center gap-2 bg-white border-2 border-green-200 rounded-full px-4 py-2 shadow-sm">
                  <Smartphone className="w-5 h-5 text-green-500" />
                  <span className="font-nunito font-medium">Partage numérique</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="/LEcoleDeNavi6.png"
                alt="Navi présente les différents formats"
                className="w-64 lg:w-80 h-auto drop-shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-fredoka text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-lg text-slate-600 font-nunito">
              Tout ce que vous devez savoir pour commencer.
            </p>
          </div>

          <div>
            {faqs.map((faq, index) => (
              <FAQItem 
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-orange-100 via-yellow-100 to-orange-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src="/LEcoleDeNavi1.png"
            alt="Navi vous invite"
            className="w-40 lg:w-52 h-auto mx-auto mb-8 drop-shadow-lg mascot-float"
          />
          <h2 className="font-fredoka text-3xl lg:text-5xl font-bold text-slate-800 mb-6">
            Prêt à retrouver du temps pour enseigner ?
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto font-nunito">
            Rejoignez les enseignants qui ont simplifié leur préparation avec L'École de Navi.
          </p>
          <button
            onClick={handleCTA}
            data-testid="final-cta"
            className="bg-orange-500 hover:bg-orange-600 text-white font-fredoka font-bold py-4 px-10 rounded-full shadow-[0_6px_0_rgb(194,65,12)] hover:shadow-[0_3px_0_rgb(194,65,12)] hover:translate-y-[3px] transition-all text-lg flex items-center gap-3 mx-auto"
          >
            Commencer maintenant
            <ArrowRight className="w-6 h-6" />
          </button>
          <p className="mt-6 text-sm text-slate-500 font-nunito">
            Accès gratuit • Pas de carte bancaire requise
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/NaviLogo.png" 
                  alt="L'École de Navi" 
                  className="h-12 w-auto"
                />
                <span className="font-fredoka font-bold text-xl text-white">
                  L'École de Navi
                </span>
              </div>
              <p className="text-slate-400 font-nunito max-w-sm">
                L'outil de création pédagogique conçu par et pour les enseignants du primaire.
              </p>
            </div>
            <div>
              <h4 className="font-fredoka font-bold text-white mb-4">Produit</h4>
              <ul className="space-y-2 font-nunito">
                <li><a href="#fonctionnalites" className="text-slate-400 hover:text-white transition-colors">Fonctionnalités</a></li>
                <li><a href="#cas-usage" className="text-slate-400 hover:text-white transition-colors">Cas d'usage</a></li>
                <li><a href="#faq" className="text-slate-400 hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-fredoka font-bold text-white mb-4">Ressources</h4>
              <ul className="space-y-2 font-nunito">
                <li><a href="/login" className="text-slate-400 hover:text-white transition-colors">Connexion</a></li>
                <li><a href="/login" className="text-slate-400 hover:text-white transition-colors">Inscription</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-12 pt-8 text-center text-slate-500 text-sm font-nunito flex items-center justify-center gap-2">
            <span>© 2024 L'École de Navi. Fait avec</span>
            <Heart className="w-4 h-4 text-red-400 fill-red-400" />
            <span>pour les enseignants.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
